/**
 * Utilities for detecting and organizing book series
 */

export interface SeriesInfo {
  seriesName: string;
  position?: number;
  total?: number;
}

export interface SeriesGroup {
  seriesName: string;
  books: any[];
  isComplete: boolean;
  totalBooks?: number;
}

// Blocklist for false-positive series names (editions, versions, etc.)
const SERIES_BLOCKLIST_PATTERNS = [
  /edition/i,
  /version/i,
  /anniversary/i,
  /revised/i,
  /updated/i,
  /expanded/i,
  /deluxe/i,
  /collector/i,
  /hardcover/i,
  /paperback/i,
  /reprint/i,
  /reissue/i,
  /illustrated/i,
  /unabridged/i,
  /abridged/i,
  /complete/i,
  /^1st$/i,
  /^2nd$/i,
  /^3rd$/i,
  /^\d+th$/i,
  /^first$/i,
  /^second$/i,
  /^third$/i,
  /persian/i,
  /spanish/i,
  /french/i,
  /german/i,
  /italian/i,
  /portuguese/i,
  /chinese/i,
  /japanese/i,
  /korean/i,
  /arabic/i,
  /russian/i,
  /hindi/i,
  /translation/i,
  /translated/i,
  /movie tie-in/i,
  /tie-in/i,
  /tv tie-in/i,
  /film edition/i,
  /international/i,
  /uk edition/i,
  /us edition/i,
  /american/i,
  /british/i,
  /canadian/i,
  /australian/i,
  /special/i,
  /limited/i,
  /signed/i,
  /numbered/i,
  /boxed/i,
  /box set/i,
  /omnibus/i,
];

// Word to number mapping
const WORD_TO_NUMBER: Record<string, number> = {
  'one': 1, 'first': 1,
  'two': 2, 'second': 2,
  'three': 3, 'third': 3,
  'four': 4, 'fourth': 4,
  'five': 5, 'fifth': 5,
  'six': 6, 'sixth': 6,
  'seven': 7, 'seventh': 7,
  'eight': 8, 'eighth': 8,
  'nine': 9, 'ninth': 9,
  'ten': 10, 'tenth': 10,
  'eleven': 11, 'eleventh': 11,
  'twelve': 12, 'twelfth': 12,
  'thirteen': 13, 'thirteenth': 13,
  'fourteen': 14, 'fourteenth': 14,
  'fifteen': 15, 'fifteenth': 15,
  'sixteen': 16, 'sixteenth': 16,
  'seventeen': 17, 'seventeenth': 17,
  'eighteen': 18, 'eighteenth': 18,
  'nineteen': 19, 'nineteenth': 19,
  'twenty': 20, 'twentieth': 20,
};

/**
 * Check if a series name is a false positive (edition, language, etc.)
 */
function isBlockedSeriesName(seriesName: string): boolean {
  return SERIES_BLOCKLIST_PATTERNS.some(pattern => pattern.test(seriesName));
}

/**
 * Convert word numbers to digits (e.g., "Book Three" -> 3)
 */
function parsePosition(positionStr: string): number | null {
  // Try numeric first
  const num = parseFloat(positionStr);
  if (!isNaN(num)) return num;
  
  // Try word-to-number
  const lower = positionStr.toLowerCase().trim();
  if (WORD_TO_NUMBER[lower]) {
    return WORD_TO_NUMBER[lower];
  }
  
  return null;
}

/**
 * Extract series information from book title
 * Handles patterns like:
 * - "Title (Series #1)"
 * - "Title (Series, Book 1)"
 * - "Title (Series Name Book Three)"
 * - "Title: Series Book 1"
 * - "Series, Book 1: Title"
 * - "Series Name #1: Title"
 * - "Title - Book 1"
 * - "Title, Part 2"
 * - "Series Name: Book One - Subtitle"
 */
export function detectSeriesFromTitle(title: string): SeriesInfo | null {
  if (!title) return null;

  // Pattern 1: (Series Name #1) or (Series Name, Book 1) or (Series Name Book Three)
  const pattern1 = /\(([^)]+?)(?:,?\s*(?:#|Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve))\)/i;
  const match1 = title.match(pattern1);
  if (match1) {
    const seriesName = match1[1].trim();
    if (!isBlockedSeriesName(seriesName)) {
      const position = parsePosition(match1[2]);
      if (position) {
        return { seriesName, position };
      }
    }
  }

  // Pattern 2: Series Name #1: Title or Series Name Book 1: Title
  const pattern2 = /^([^:#]+?)(?:#|Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten)\s*[:-]/i;
  const match2 = title.match(pattern2);
  if (match2) {
    const seriesName = match2[1].trim();
    if (!isBlockedSeriesName(seriesName)) {
      const position = parsePosition(match2[2]);
      if (position) {
        return { seriesName, position };
      }
    }
  }

  // Pattern 3: Title - Book 1 or Title, Part 2
  const pattern3 = /[,\-]\s*(?:Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten)$/i;
  const match3 = title.match(pattern3);
  if (match3) {
    const baseTitleMatch = title.match(/^(.+?)\s*[,\-]\s*(?:Book|Part|Volume|Vol\.?)/i);
    if (baseTitleMatch) {
      const seriesName = baseTitleMatch[1].trim();
      if (!isBlockedSeriesName(seriesName)) {
        const position = parsePosition(match3[1]);
        if (position) {
          return { seriesName, position };
        }
      }
    }
  }

  // Pattern 4: "Series Name: Book One - Subtitle" or "Series Name: Book 1"
  const pattern4 = /^(.+?):\s*(?:Book|Part|Volume)\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten)(?:\s*[-–—]|$)/i;
  const match4 = title.match(pattern4);
  if (match4) {
    const seriesName = match4[1].trim();
    if (!isBlockedSeriesName(seriesName)) {
      const position = parsePosition(match4[2]);
      if (position) {
        return { seriesName, position };
      }
    }
  }

  // Pattern 5: "Title (Series #1 of 5)" - extract total
  const pattern5 = /\(([^)]+?)(?:,?\s*(?:#|Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?)\s*(?:of|\/)\s*(\d+))\)/i;
  const match5 = title.match(pattern5);
  if (match5) {
    const seriesName = match5[1].trim();
    if (!isBlockedSeriesName(seriesName)) {
      const position = parsePosition(match5[2]);
      const total = parseInt(match5[3], 10);
      if (position) {
        return { seriesName, position, total };
      }
    }
  }

  // Pattern 6: "[Series Name #1]" with brackets
  const pattern6 = /\[([^\]]+?)(?:,?\s*(?:#|Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?))\]/i;
  const match6 = title.match(pattern6);
  if (match6) {
    const seriesName = match6[1].trim();
    if (!isBlockedSeriesName(seriesName)) {
      const position = parsePosition(match6[2]);
      if (position) {
        return { seriesName, position };
      }
    }
  }

  return null;
}

/**
 * Group books by series
 */
export function groupBooksBySeries(books: any[]): {
  seriesGroups: SeriesGroup[];
  standaloneBooks: any[];
} {
  const seriesMap = new Map<string, any[]>();
  const standaloneBooks: any[] = [];

  books.forEach(book => {
    // Check if book has explicit series info
    if (book.series_name && !isBlockedSeriesName(book.series_name)) {
      const key = book.series_name.toLowerCase();
      if (!seriesMap.has(key)) {
        seriesMap.set(key, []);
      }
      seriesMap.get(key)!.push(book);
    } else {
      // Try to detect series from title
      const seriesInfo = detectSeriesFromTitle(book.title);
      if (seriesInfo) {
        const key = seriesInfo.seriesName.toLowerCase();
        if (!seriesMap.has(key)) {
          seriesMap.set(key, []);
        }
        // Attach detected series info to book
        seriesMap.get(key)!.push({
          ...book,
          detectedSeriesName: seriesInfo.seriesName,
          detectedPosition: seriesInfo.position
        });
      } else {
        standaloneBooks.push(book);
      }
    }
  });

  // Convert map to sorted series groups
  const seriesGroups: SeriesGroup[] = Array.from(seriesMap.entries())
    .filter(([_, books]) => books.length > 1) // Only include series with multiple books
    .map(([key, books]) => {
      // Sort books by position
      const sortedBooks = books.sort((a, b) => {
        const posA = a.series_position ?? a.detectedPosition ?? 0;
        const posB = b.series_position ?? b.detectedPosition ?? 0;
        return posA - posB;
      });

      const firstBook = sortedBooks[0];
      const seriesName = firstBook.series_name || firstBook.detectedSeriesName;
      const totalBooks = firstBook.series_total;
      
      return {
        seriesName,
        books: sortedBooks,
        isComplete: totalBooks ? sortedBooks.length === totalBooks : false,
        totalBooks
      };
    })
    .sort((a, b) => {
      // Sort series by earliest published year
      const yearA = Math.min(...a.books.map(b => b.published_year || 9999));
      const yearB = Math.min(...b.books.map(b => b.published_year || 9999));
      return yearA - yearB;
    });

  // Add books from series with only 1 book to standaloneBooks
  seriesMap.forEach((books, key) => {
    if (books.length === 1) {
      standaloneBooks.push(books[0]);
    }
  });

  return { seriesGroups, standaloneBooks };
}

/**
 * Get clean title without series information
 */
export function getCleanTitle(title: string, seriesName?: string): string {
  if (!title) return '';
  
  let cleanTitle = title;
  
  // Remove (Series Name #1) pattern
  cleanTitle = cleanTitle.replace(/\([^)]+?(?:#|Book|Part|Volume|Vol\.?)\s*\d+(?:\.\d+)?\)/gi, '');
  
  // Remove [Series Name #1] pattern
  cleanTitle = cleanTitle.replace(/\[[^\]]+?(?:#|Book|Part|Volume|Vol\.?)\s*\d+(?:\.\d+)?\]/gi, '');
  
  // Remove Series Name #1: pattern
  if (seriesName) {
    const escapedSeriesName = seriesName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escapedSeriesName}\\s*(?:#|Book|Part|Volume|Vol\\.?)\\s*\\d+(?:\\.\\d+)?\\s*[:-]\\s*`, 'i');
    cleanTitle = cleanTitle.replace(pattern, '');
  }
  
  // Remove trailing book numbers
  cleanTitle = cleanTitle.replace(/[,\-]\s*(?:Book|Part|Volume|Vol\.?)\s*\d+(?:\.\d+)?$/i, '');
  
  return cleanTitle.trim();
}

/**
 * Validate and clean series data from external sources
 */
export function validateSeriesData(seriesName: string | null, seriesPosition: number | null): {
  isValid: boolean;
  cleanSeriesName: string | null;
  cleanPosition: number | null;
} {
  if (!seriesName) {
    return { isValid: false, cleanSeriesName: null, cleanPosition: null };
  }
  
  // Check if series name is blocked
  if (isBlockedSeriesName(seriesName)) {
    return { isValid: false, cleanSeriesName: null, cleanPosition: null };
  }
  
  // Clean series name
  const cleanSeriesName = seriesName.trim()
    .replace(/\s+/g, ' ')
    .replace(/^the\s+/i, 'The ');
  
  // Validate position
  const cleanPosition = seriesPosition && seriesPosition > 0 && seriesPosition <= 100 
    ? seriesPosition 
    : null;
  
  return {
    isValid: cleanSeriesName.length > 1 && cleanSeriesName.length < 200,
    cleanSeriesName,
    cleanPosition
  };
}
